import { useQueryClient } from "@tanstack/react-query";
import { message, Row, Typography } from "antd";
import React, { useMemo, useState } from "react";
import RSelect from "react-select";
import { SelectOption } from "../../../../../types/select";
import { getSelectPlaceholderMessage } from "../../../../libs/form";
import { findSelectOption } from "../../../../libs/utils";
import Button from "../../../../molecules/Button";
import { changeServiceRequestStatus, ChangeStatusRequest } from "../api";
import { ServiceRequestGrid } from "../types";

export const statusOptions: SelectOption[] = [
  { label: "Awaiting Payment", value: "awaiting_payment" },
  { label: "Requested", value: "requested" },
  { label: "In Progress", value: "in_progress" },
  { label: "Completed", value: "completed" },
  { label: "Closed Without Resolution", value: "closed_without_resolution" },
];

type ChangeStatusSectionProps = {
  selectedRow: ServiceRequestGrid;
  onSuccess: () => void;
};

export const ChangeStatusSection = (props: ChangeStatusSectionProps) => {
  const { selectedRow, onSuccess } = props;

  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState<SelectOption>(findSelectOption(statusOptions, selectedRow?.status));

  const filteredStatusOptions = useMemo(() => {
    if (!selectedRow?.status) return statusOptions;

    const statusOrder = ["awaiting_payment", "requested", "in_progress", "completed", "closed_without_resolution"];
    const currentIndex = statusOrder.indexOf(selectedRow.status);
    return statusOptions.filter((option) => statusOrder.indexOf(option.value as string) > currentIndex);
  }, [selectedRow?.status]);

  const handleChangeStatus = async () => {
    try {
      setIsSubmitting(true);

      const payload: ChangeStatusRequest = {
        ticketId: selectedRow?.ticketId,
        ticketStatus: status?.value,
      };
      await changeServiceRequestStatus(payload);
      message.success("Status changed successfully");
      queryClient.invalidateQueries({ queryKey: ["service-requests"] });
      onSuccess?.();
    } catch (error) {
      message.error(error?.response?.data?.error ?? "Error occurred while updating status");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <Row>
        <Typography.Text>Status</Typography.Text>
        <RSelect
          className="w-100"
          value={status}
          placeholder={getSelectPlaceholderMessage("status")}
          onChange={(selectedOption) => setStatus(selectedOption)}
          options={filteredStatusOptions}
          styles={{
            control: (styles) => ({ ...styles, borderRadius: 6 }),
            valueContainer: (styles) => ({ ...styles, textTransform: "capitalize" }),
            dropdownIndicator: (styles) => ({ ...styles, padding: 5 }),
            indicatorSeparator: (styles) => ({ ...styles, backgroundColor: "#fff" }),
            option: (styles) => ({ ...styles, textTransform: "capitalize" }),
            placeholder: (styles) => ({ ...styles, color: "rgba(0,0,0,.3)" }),
            menuPortal: (styles) => ({ ...styles, zIndex: 99999999 }),
          }}
          theme={(styles) => ({ ...styles, borderRadius: 2, colors: { ...styles.colors, primary: "#767676" } })}
        />
      </Row>

      <Row style={{ display: "flex", justifyContent: "end", marginTop: "20px" }}>
        <Button
          style={{
            height: "2.25rem",
            width: "7rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          label={isSubmitting ? "Updating..." : "Update"}
          disabled={isSubmitting}
          onClick={handleChangeStatus}
        />
      </Row>
    </div>
  );
};
